import { useMemo, useState } from 'react';
import { tx } from '../i18n';
import { configuredTileLayer, groundResolution, tilesFor, worldX, worldY, zoomFor } from '../lib/tiles';
import { num } from '../lib/values';
import type { NearbySchool, NearbySchools, NearbyTransport, TransportStop } from '../types/api';

interface SurroundingsMapProps {
  latitude: number;
  longitude: number;
  addressLine: string;
  schools: NearbySchools | null | undefined;
  transport: NearbyTransport | null | undefined;
}

/** About how many pixels across the square should be; the zoom is chosen to land near it. */
const TARGET_PX = 768;

/** The three kinds of thing on this map, and how each one is drawn. */
type Layer = 'schools' | 'stations' | 'buses';

interface Mark {
  id: string;
  layer: Layer;
  name: string;
  detail: string;
  latitude: number;
  longitude: number;
  distanceMetres: number;
}

/**
 * What is around the address, drawn where it actually is.
 *
 * <p>The report already lists the schools and stops with their distances. A distance is a number
 * you have to hold three of at once to picture a place; a map answers "are they all one way?" and
 * "is the station on the far side of the park?" in a glance, which no list does. So this is the
 * same facts arranged the other way, not new facts - and the lists stay, because they carry the
 * Ofsted grades, the modes and the walking times, and they are where the keyboard goes.
 *
 * <p>Every mark is a published coordinate: the school register publishes school positions and
 * NaPTAN publishes stop positions. Nothing is drawn between them - no catchments, no shaded
 * reach, no routes. The rings are the radii that were searched, dashed because a straight line
 * from a doorway is not a walk, and labelled with their own distance so the scale is readable
 * without a scale bar.
 *
 * <p>Each kind has its own shape as well as its own colour - a house, a square, a triangle, a
 * circle - because a map that separates its marks by colour alone separates nothing for a reader
 * who cannot tell the colours apart.
 */
export default function SurroundingsMap({
  latitude,
  longitude,
  addressLine,
  schools,
  transport,
}: SurroundingsMapProps) {
  const [hidden, setHidden] = useState<Layer[]>([]);
  const [hover, setHover] = useState<Mark | null>(null);

  const centreLat = num(latitude);
  const centreLon = num(longitude);

  const marks = useMemo<Mark[]>(() => {
    const out: Mark[] = [];
    const school = (row: NearbySchool, phase: string) => {
      if (num(row.latitude) === null || num(row.longitude) === null) {
        return;
      }
      out.push({
        id: `school-${row.urn}`,
        layer: 'schools',
        name: row.name,
        detail: `${phase} · ${row.distanceMetres} m${row.ofsted?.headline ? ` · ${row.ofsted.headline}` : ''}`,
        latitude: row.latitude,
        longitude: row.longitude,
        distanceMetres: row.distanceMetres,
      });
    };
    (schools?.primary ?? []).forEach((row) =>
      school(row, tx('Primary', 'İlkokul', 'Podstawowa', 'Primária', 'Primaria')),
    );
    (schools?.secondary ?? []).forEach((row) =>
      school(row, tx('Secondary', 'Ortaokul / lise', 'Ponadpodstawowa', 'Secundária', 'Secundaria')),
    );

    const stop = (row: TransportStop, layer: Layer, index: number) => {
      if (num(row.latitude) === null || num(row.longitude) === null) {
        return;
      }
      out.push({
        id: `${layer}-${index}-${row.name}`,
        layer,
        name: row.name,
        detail: `${row.modeLabel} · ${row.distanceMetres} m`,
        latitude: row.latitude,
        longitude: row.longitude,
        distanceMetres: row.distanceMetres,
      });
    };
    (transport?.stations ?? []).forEach((row, index) => stop(row, 'stations', index));
    (transport?.busStops ?? []).forEach((row, index) => stop(row, 'buses', index));

    // A school and a school-gate bus stop can share a spot; drawing the nearer thing last keeps
    // the one the reader is most likely to be looking for on top.
    return out.sort((a, b) => b.distanceMetres - a.distanceMetres);
  }, [schools, transport]);

  if (centreLat === null || centreLon === null || marks.length === 0) {
    return null;
  }

  const shown = marks.filter((mark) => !hidden.includes(mark.layer));

  // The frame holds everything drawn, with a little air: the furthest mark still shown, or the
  // furthest ring, whichever reaches further. It never shrinks below 400 m, so a corner shop of
  // an address with one bus stop outside does not fill the screen at street level.
  const rings = [
    { metres: num(schools?.radiusMetres), layer: 'schools' as Layer },
    { metres: num(transport?.stationRadiusMetres), layer: 'stations' as Layer },
    { metres: num(transport?.busRadiusMetres), layer: 'buses' as Layer },
  ].filter((ring): ring is { metres: number; layer: Layer } => ring.metres !== null && !hidden.includes(ring.layer));

  const furthest = Math.max(
    400,
    ...shown.map((mark) => mark.distanceMetres),
    ...rings.map((ring) => ring.metres),
  );
  const halfMetres = furthest * 1.12;

  /*
   * Web Mercator, at a whole zoom level, even when no backdrop is configured. It is the
   * projection every tile service publishes in, so working in it is what lets the tiles and the
   * marks be placed by one transform rather than two that agree approximately. A whole zoom
   * rather than a fitted scale because a tile is only published at whole zooms; the square is
   * then whatever size that zoom makes it, near enough to the target.
   */
  const zoom = zoomFor(centreLat, halfMetres, TARGET_PX);
  const metresPerPixel = groundResolution(centreLat, zoom);
  const size = Math.round((2 * halfMetres) / metresPerPixel);
  const left = worldX(centreLon, zoom) - size / 2;
  const top = worldY(centreLat, zoom) - size / 2;

  const x = (lon: number) => worldX(lon, zoom) - left;
  const y = (lat: number) => worldY(lat, zoom) - top;
  // A ground circle is very slightly not a circle in Mercator; over two kilometres the difference
  // is a fiftieth of a pixel, so the centre's own resolution is the honest one to draw it at.
  const radius = (metres: number) => metres / metresPerPixel;

  const tileLayer = configuredTileLayer();
  const tiles = tileLayer ? tilesFor(tileLayer, zoom, left, top, size) : [];

  const counts = {
    schools: marks.filter((mark) => mark.layer === 'schools').length,
    stations: marks.filter((mark) => mark.layer === 'stations').length,
    buses: marks.filter((mark) => mark.layer === 'buses').length,
  };

  const summary = tx(
    `Map of what is around ${addressLine}: ${counts.schools} schools, ${counts.stations} stations and ${counts.buses} bus stops, each drawn at its own published position. Every name and distance is in the lists below.`,
    `${addressLine} çevresinin haritası: ${counts.schools} okul, ${counts.stations} istasyon ve ${counts.buses} otobüs durağı, her biri kendi yayımlanmış konumunda. Tüm isimler ve mesafeler aşağıdaki listelerde.`,
    `Mapa okolicy ${addressLine}: ${counts.schools} szkół, ${counts.stations} stacji i ${counts.buses} przystanków, każde w swojej opublikowanej pozycji. Wszystkie nazwy i odległości są na listach poniżej.`,
    `Mapa do que rodeia ${addressLine}: ${counts.schools} escolas, ${counts.stations} estações e ${counts.buses} paragens, cada uma na sua posição publicada. Todos os nomes e distâncias estão nas listas abaixo.`,
    `Mapa de lo que rodea a ${addressLine}: ${counts.schools} colegios, ${counts.stations} estaciones y ${counts.buses} paradas, cada uno en su posición publicada. Todos los nombres y distancias están en las listas de abajo.`,
  );

  const toggle = (layer: Layer) =>
    setHidden((current) =>
      current.includes(layer) ? current.filter((item) => item !== layer) : [...current, layer],
    );

  const layerName = (layer: Layer) => {
    switch (layer) {
      case 'schools':
        return tx('Schools', 'Okullar', 'Szkoły', 'Escolas', 'Colegios');
      case 'stations':
        return tx('Stations', 'İstasyonlar', 'Stacje', 'Estações', 'Estaciones');
      default:
        return tx('Bus stops', 'Otobüs durakları', 'Przystanki', 'Paragens', 'Paradas');
    }
  };

  /**
   * The legend's swatch is the map's own mark, drawn small.
   *
   * <p>Not an icon that stands for it: a key whose square is drawn by different code from the
   * square on the map is a key that can drift out of agreement with it, and a legend that
   * disagrees with its map is worse than none.
   */
  const Key = ({ layer }: { layer: Layer }) => (
    <svg className="surroundings__key" viewBox="0 0 20 20" aria-hidden="true">
      {layer === 'schools' ? (
        <rect className="surroundings__mark surroundings__mark--schools" x="2" y="2" width="16" height="16" rx="4" />
      ) : layer === 'stations' ? (
        <polygon className="surroundings__mark surroundings__mark--stations" points="10,2 19,17 1,17" />
      ) : (
        <circle className="surroundings__mark surroundings__mark--buses" cx="10" cy="10" r="7" />
      )}
    </svg>
  );

  return (
    <figure className="surroundings">
      <div className="surroundings__frame">
        {/* The backdrop, behind everything and out of the accessibility tree: it is context, and
            every fact on this map is a mark placed over it. */}
        {tiles.length > 0 ? (
          <div className="surroundings__tiles" aria-hidden="true">
            {tiles.map((tile) => (
              <img
                key={tile.key}
                src={tile.url}
                alt=""
                loading="lazy"
                width={256}
                height={256}
                style={{
                  left: `${(tile.left / size) * 100}%`,
                  top: `${(tile.top / size) * 100}%`,
                  width: `${(256 / size) * 100}%`,
                  height: `${(256 / size) * 100}%`,
                }}
              />
            ))}
          </div>
        ) : null}

        <svg
          viewBox={`0 0 ${size} ${size}`}
          className={tiles.length > 0 ? 'surroundings__svg surroundings__svg--over-tiles' : 'surroundings__svg'}
          role="img"
          aria-label={summary}
          onMouseLeave={() => setHover(null)}
        >
          {/* The radii that were searched. Dashed and unfilled: a filled disc reads as a
              catchment, and no register publishes one. */}
          <g className="surroundings__rings">
            {rings.map((ring) => (
              <circle key={`${ring.layer}-${ring.metres}`} cx={size / 2} cy={size / 2} r={radius(ring.metres)} />
            ))}
          </g>

          <g className="surroundings__marks">
            {shown.map((mark) => {
              const cx = x(mark.longitude);
              const cy = y(mark.latitude);
              const common = {
                className: `surroundings__mark surroundings__mark--${mark.layer}${hover?.id === mark.id ? ' is-hovered' : ''}`,
                onMouseEnter: () => setHover(mark),
              };
              return (
                <g key={mark.id}>
                  {mark.layer === 'schools' ? (
                    <rect {...common} x={cx - 9} y={cy - 9} width={18} height={18} rx={4} />
                  ) : mark.layer === 'stations' ? (
                    <polygon {...common} points={`${cx},${cy - 11} ${cx + 10},${cy + 7} ${cx - 10},${cy + 7}`} />
                  ) : (
                    <circle {...common} cx={cx} cy={cy} r={7} />
                  )}
                  <title>{`${mark.name} — ${mark.detail}`}</title>
                </g>
              );
            })}
          </g>

          {/* The address last and largest: it is the thing every distance is measured from. */}
          <g className="surroundings__here">
            <circle cx={size / 2} cy={size / 2} r={13} className="surroundings__here-halo" />
            <rect
              className="surroundings__here-mark"
              x={size / 2 - 7}
              y={size / 2 - 7}
              width={14}
              height={14}
              transform={`rotate(45 ${size / 2} ${size / 2})`}
            />
            <title>{addressLine}</title>
          </g>
        </svg>

        {/* The ring distances are HTML, not SVG text: text inside the drawing scales with the
            drawing, so a label that is right at 560px is five pixels tall on a phone. Placed by
            percentage, they sit on their own ring at every width and stay at the page's own
            type size. */}
        {rings.map((ring) => (
          <span
            key={`label-${ring.layer}-${ring.metres}`}
            className="surroundings__ring-label"
            style={{ left: '50%', top: `${((size / 2 - radius(ring.metres)) / size) * 100}%` }}
          >
            {ring.metres >= 1000 ? `${(ring.metres / 1000).toFixed(1)} km` : `${ring.metres} m`}
          </span>
        ))}

        {/* The provider's credit, on the map and in its usual corner. Every tile service requires
            it there rather than somewhere a reader might not look; it is never behind a toggle,
            and the layer is not drawn at all without it. */}
        {tileLayer ? (
          <p className="surroundings__credit">
            {tileLayer.attributionHref ? (
              <a href={tileLayer.attributionHref} target="_blank" rel="noreferrer noopener">
                {tileLayer.attribution}
              </a>
            ) : (
              tileLayer.attribution
            )}
          </p>
        ) : null}

        {hover ? (
          <div
            className="surroundings__tip"
            style={{
              left: `${(x(hover.longitude) / size) * 100}%`,
              top: `${(y(hover.latitude) / size) * 100}%`,
            }}
          >
            <strong>{hover.name}</strong>
            <span className="small muted">{hover.detail}</span>
          </div>
        ) : null}
      </div>

      <figcaption className="surroundings__caption">
        <ul className="surroundings__legend">
          <li>
            <svg className="surroundings__key" viewBox="0 0 20 20" aria-hidden="true">
              <circle className="surroundings__here-halo" cx="10" cy="10" r="9" />
              <rect
                className="surroundings__here-mark"
                x="5"
                y="5"
                width="10"
                height="10"
                transform="rotate(45 10 10)"
              />
            </svg>
            {tx('This address', 'Bu adres', 'Ten adres', 'Esta morada', 'Esta dirección')}
          </li>
          {(['schools', 'stations', 'buses'] as Layer[])
            .filter((layer) => counts[layer] > 0)
            .map((layer) => (
              <li key={layer}>
                {/* A legend entry that switches its own layer off: the only thing a reader wants
                    to do with a crowded map is take something out of it. */}
                <button
                  type="button"
                  className={hidden.includes(layer) ? 'surroundings__toggle is-off' : 'surroundings__toggle'}
                  aria-pressed={!hidden.includes(layer)}
                  onClick={() => toggle(layer)}
                >
                  <Key layer={layer} />
                  {layerName(layer)} ({counts[layer]})
                </button>
              </li>
            ))}
        </ul>
        <p className="small muted">
          {tx(
            'Each mark is a published position: schools from the school register, stops from NaPTAN. The rings are how far was searched, in a straight line from the address — not a walking route, and not a catchment.',
            'Her işaret yayımlanmış bir konumdur: okullar okul kayıtlarından, duraklar NaPTAN’dan. Halkalar, adresten düz çizgiyle ne kadar uzağa bakıldığını gösterir — yürüme rotası değildir, kayıt bölgesi de değildir.',
            'Każdy znacznik to opublikowana pozycja: szkoły z rejestru szkół, przystanki z NaPTAN. Okręgi pokazują, jak daleko szukano w linii prostej od adresu — to nie trasa piesza ani rejon szkolny.',
            'Cada marca é uma posição publicada: escolas do registo de escolas, paragens do NaPTAN. Os círculos mostram até onde se procurou, em linha reta a partir da morada — não é um percurso a pé nem uma área de influência.',
            'Cada marca es una posición publicada: colegios del registro escolar, paradas de NaPTAN. Los círculos indican hasta dónde se buscó, en línea recta desde la dirección: no es una ruta a pie ni una zona de escolarización.',
          )}
        </p>
      </figcaption>
    </figure>
  );
}
